import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";

const SESSION_KEY = "jw_visit_session";
const LAST_PING_KEY = "jw_visit_last";
const DEBOUNCE_MS = 1500;       // collapse rapid route changes
const REPING_THROTTLE_MS = 30_000; // don't repeatedly track same page

function getOrCreateSession() {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
        id = "s_" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
        sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
}

/**
 * Mount once in <App />. Sends one analytics ping per route change (debounced),
 * skipping admin URLs entirely so internal traffic doesn't pollute the funnel.
 */
export default function VisitTracker() {
    const { pathname, search } = useLocation();
    const timer = useRef(null);
    const lastKey = useRef("");

    useEffect(() => {
        if (pathname.startsWith("/admin")) return;
        const key = pathname + search;
        if (key === lastKey.current) return;
        lastKey.current = key;

        // Throttle if a recent ping for the same page is still warm.
        const cached = sessionStorage.getItem(LAST_PING_KEY);
        if (cached) {
            try {
                const [page, ts] = JSON.parse(cached);
                if (page === key && Date.now() - Number(ts) < REPING_THROTTLE_MS) return;
            } catch { /* noop */ }
        }

        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(() => {
            const session_id = getOrCreateSession();
            const body = {
                session_id,
                page: key.slice(0, 200),
                referrer: document.referrer || null,
                user_agent: navigator.userAgent || null,
                language: navigator.language || null,
                screen: `${window.innerWidth}x${window.innerHeight}`,
            };
            api.post("/analytics/visit", body).catch(() => {});
            sessionStorage.setItem(LAST_PING_KEY, JSON.stringify([key, Date.now()]));
        }, DEBOUNCE_MS);

        return () => {
            if (timer.current) clearTimeout(timer.current);
        };
    }, [pathname, search]);

    return null;
}
